import {
  CAlert,
  CButton,
  CCol,
  CForm,
  CFormGroup,
  CInput,
  // CInputCheckbox,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CTextarea,
} from "@coreui/react";
import { useDownloadDocumentPreview } from "src/views/datasets/services/DocumentServices";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  List,
  ListItem,
  Typography,
  Paper,
  Grid,
} from "@mui/material";
import PDFViewer from "src/views/reports/PDFViewer";

import ReactQueryLoading from "src/reusable/ReactQueryLoading";
import { authorizedUser } from "src/helpers/Auth";
import { nuclearn_api_url } from "src/App";
import { ReportType } from "src/interfaces/enums";
import React, { useEffect, useState, useRef } from "react";
import {
  useUploadReportTemplate,
  useCreateReport,
  useDeleteReport,
  useReportTemplateList,
  useUpdateReport,
  useUpdateReportTemplate,
  useDeleteReportTemplate,
  useGenerateTemplateDoc,
  useGetReportTemplateArtifacts,
} from "../reports/ReportServices";

import CIcon from "@coreui/icons-react";
import { ListSelect } from "src/reusable";
import { Loading } from "src/reusable/LoadingSpinner";
import LoadingOverlay from "react-loading-overlay-ts";
import PropTypes from "prop-types";
import { useBucketList } from "../buckets/BucketServices";
import { DeleteItemModal } from "src/reusable/components/DeleteItemModal";
import { determineLoginPlatformType } from "src/reusable/Functions";
import { PulseLoader } from "react-spinners";
import { createPortal } from "react-dom";

const BucketSelect = (props) => {
  const { data: bucketList } = useBucketList({});
  const [displayItems, setDisplayItems] = useState([]);
  const [selectedBuckets, setSelectedBuckets] = useState([]);
  const [bucketId2AnalyticId, setBucketMap] = useState(new Map());

  useEffect(() => {
    //upon initial data load
    // filter the display item properties
    // create map of bucket to analytic
    if (bucketList) {
      initializeDisplayItems(setDisplayItems, bucketList);
      bucketList.map((bucket) =>
        bucketId2AnalyticId.set(String(bucket.id), bucket.analytic_fk),
      );
      setBucketMap(bucketId2AnalyticId);
    }
  }, [bucketList]);

  useEffect(() => {
    //reset the displayed items
    if (!selectedBuckets || selectedBuckets?.length === 0) {
      // invalidate buckets and reset the display list to the original state with all the items

      initializeDisplayItems(setDisplayItems, bucketList);
    } else if (selectedBuckets?.length === 1) {
      const selectedBucketId = selectedBuckets[0];
      const selectedAnalyticId = bucketId2AnalyticId.get(selectedBucketId);

      let filteredDisplayItems = displayItems.filter((displayItem) => {
        let displayItemAnalyticId = bucketId2AnalyticId.get(
          String(displayItem.id),
        );
        return displayItemAnalyticId === selectedAnalyticId;
      });
      setDisplayItems(filteredDisplayItems);
      props.handleInvalidSelectionMessage("");
      props.handleSelectedBuckets(selectedBuckets);
    } else {
      let analyticIdsEqualityCheck = selectedBuckets.every((bucketId) => {
        return (
          bucketId2AnalyticId.get(bucketId) ===
          bucketId2AnalyticId.get(selectedBuckets[0])
        );
      });
      if (analyticIdsEqualityCheck) {
        props.handleInvalidSelectionMessage("");
        props.handleSelectedBuckets(selectedBuckets);
      } else {
        props.handleInvalidSelectionMessage(
          "Buckets selected need to be generated from the same dataset and analysis options, try selecting only one bucket first.",
        );
      }
    }
  }, [selectedBuckets]);

  return (
    <>
      <ListSelect
        handleSelectedValue={(x) => {
          setSelectedBuckets(x);
        }}
        items={displayItems || []}
        selectDomID={"bucket-selector"}
        disabled={!displayItems || props.disabled}
        multiple={true}
        selectedValue={props.selectedBuckets}
        height={`${Math.min(displayItems?.length * 1.8, 1.5 * 15)}em`}
      />
    </>
  );
};

BucketSelect.propTypes = {
  disabled: PropTypes.bool,
  handleSelectedBuckets: PropTypes.func.isRequired,
  handleInvalidSelectionMessage: PropTypes.func.isRequired,
  selectedBuckets: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
};

BucketSelect.defaultProps = {
  disabled: false,
};

const ReportTemplateSelect = (props) => {
  const { data: reportTemplateList } = useReportTemplateList(props.reportType);
  let displayItems = [];
  if (reportTemplateList) {
    displayItems = reportTemplateList.map((reportTemplate) => {
      return {
        id: reportTemplate.id,
        name: reportTemplate.name,
      };
    });
  }
  return (
    <ListSelect
      handleSelectedValue={(x) => {
        props.handleSelectedReportTemplate(x);
      }}
      items={displayItems}
      selectDomID={"report-template-selector"}
      disabled={!displayItems || props.disabled}
      multiple={false}
      selectedValue={props.selectedReportTemplate}
    />
  );
};

ReportTemplateSelect.propTypes = {
  disabled: PropTypes.bool,
  handleSelectedReportTemplate: PropTypes.func.isRequired,
  selectedReportTemplate: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  reportType: PropTypes.oneOf([ReportType.ANALYTIC, ReportType.GENERATIVE]),
};

ReportTemplateSelect.defaultProps = {
  disabled: false,
  reportType: ReportType.ANALYTIC,
};

const validateReport = (report) => {
  let errors = [];
  if (report.name.length === 0) {
    // validate name
    errors.push("Name cannot be empty.");
  }
  if (!report.bucket_fks || report.bucket_fks.length === 0) {
    // validate parent fks
    errors.push("Report needs to be associated with at least one bucket.");
  }
  if (!report.report_template_fk) {
    // validate report template exists
    errors.push("Report needs to be associated with at least one template.");
  }
  return errors;
};

const ReportEditor = (props) => {
  const [name, setName] = useState(props.report.name);
  const [description, setDescription] = useState(props.report.description);
  const [bucketFks, setBucketFks] = useState(props.report.bucket_fks);
  const [reportTemplateFk, setReportTemplateFk] = useState(
    props.report.report_template_fk,
  );

  const [invalidBucketSelectionMessage, setInvalidBucketSelectionMessage] =
    useState("");

  const handleInvalidSelectionMessage = (message) => {
    setInvalidBucketSelectionMessage(message);
  };

  const handleSelectedBuckets = (x) => {
    setBucketFks(x);
    handleInvalidSelectionMessage("");
  };

  useEffect(() => {
    props.onChange({
      name: name,
      description: description,
      bucket_fks: bucketFks,
      report_template_fk: reportTemplateFk,
    });
  }, [name, description, bucketFks, reportTemplateFk]);

  return (
    <CForm>
      <CFormGroup row>
        <CLabel className="col-sm-3 col-form-label">Name</CLabel>
        <CCol sm={9}>
          {props.mode === "create" ? (
            <CInput
              id="edit-report-name-input"
              type="text"
              name="name"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          ) : (
            <CLabel
              id="edit-report-name-label"
              className="col-sm-9 col-form-label"
            >
              {name}
            </CLabel>
          )}
        </CCol>
      </CFormGroup>
      <CFormGroup row>
        <CLabel className="col-sm-3 col-form-label">Description</CLabel>
        {props.mode === "view" ? (
          <CLabel
            id="edit-report-description-label"
            className="col-sm-9 col-form-label"
          >
            {description}
          </CLabel>
        ) : (
          <CCol sm={9}>
            <CTextarea
              id="edit-report-description-textarea"
              type="text"
              rows="2"
              name="description"
              placeholder="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </CCol>
        )}
      </CFormGroup>
      <CFormGroup row>
        <CLabel className="col-sm-3 col-form-label">Bucket(s)</CLabel>
        <CCol sm={9}>
          <BucketSelect
            id="edit-report-buckets-select"
            handleSelectedBuckets={handleSelectedBuckets}
            selectedBuckets={bucketFks}
            disabled={props.mode === "view"}
            handleInvalidSelectionMessage={handleInvalidSelectionMessage}
          />
        </CCol>
      </CFormGroup>

      {invalidBucketSelectionMessage && (
        <CFormGroup row>
          <div>
            <CAlert color="info" style={{ margin: "0px 10px 0px 10px" }}>
              {`${invalidBucketSelectionMessage}`}
            </CAlert>
          </div>
        </CFormGroup>
      )}

      <CFormGroup row>
        <CLabel className="col-sm-3 col-form-label">Report Template(s)</CLabel>
        <CCol sm={9}>
          <ReportTemplateSelect
            id="edit-report-template-select"
            reportType={props.reportType}
            handleSelectedReportTemplate={(x) => {
              setReportTemplateFk(x);
            }}
            selectedReportTemplate={reportTemplateFk}
            disabled={props.mode === "view"}
          />
        </CCol>
      </CFormGroup>
    </CForm>
  );
};

ReportEditor.propTypes = {
  mode: PropTypes.oneOf(["create", "edit", "view"]).isRequired,
  report: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    bucket_fks: PropTypes.arrayOf(PropTypes.string).isRequired,
    report_template_fk: PropTypes.string.isRequired,
  }),
  onChange: PropTypes.func.isRequired,
  reportType: PropTypes.oneOf([ReportType.ANALYTIC, ReportType.GENERATIVE]),
};

ReportEditor.defaultProps = {
  report: {
    name: "",
    description: "",
    parent_fks: [],
    report_template_fk: "",
  },
  reportType: ReportType.ANALYTIC,
};

export const ReportTemplateSelector = (props) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const { data, isLoading } = useReportTemplateList(props.reportType);

  const handleTemplateChange = (id) => {
    const template = data.find((element) => element.id === Number(id));
    setSelectedTemplate(template);
    props.onChange(template);
  };

  return (
    <CForm>
      <CFormGroup row>
        <CLabel className="col-sm-3 col-form-label">Select Template</CLabel>
        <CCol sm={9}>
          <ListSelect
            selectDomID="report-template-selector"
            handleSelectedValue={(x) => handleTemplateChange(x)}
            items={isLoading ? [{ name: "LOADING", id: 0 }] : data}
          />
        </CCol>
      </CFormGroup>
      {selectedTemplate && (
        <CAlert color="info">Selected Template: {selectedTemplate.name}</CAlert>
      )}
    </CForm>
  );
};

ReportTemplateSelector.propTypes = {
  onChange: PropTypes.func.isRequired,
  reportType: PropTypes.oneOf([ReportType.ANALYTIC, ReportType.GENERATIVE])
    .isRequired,
};

const ReportTemplateEditor = (props) => {
  const [templateName, setTemplateName] = useState(props.report.name);
  const [templateDescription, setTemplateDescription] = useState(
    props.report.description,
  );
  const [fileUploaded, setFileUploaded] = useState(false);
  const [fileAccepted, setFileAccepted] = useState(false);
  const [templateFile, setTemplateFile] = useState("");
  const [filename, setFilename] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const fileInput = useRef(null);

  const { data, isLoading } = useReportTemplateList(props.reportType);

  const handleTemplatedChanged = (id) => {
    const template = data.find((element) => element.id === Number(id));
    if (template) {
      setSelectedTemplate(template);
      setTemplateName(template.name);
    } else {
      setTemplateName("");
      setTemplateFile("");
      setSelectedTemplate(null);
      setTemplateDescription("");
    }
    setFileUploaded(false);
    setFileAccepted(false);
  };

  useEffect(() => {
    props.onChange({
      templateName: templateName,
      templateDescription: templateDescription,
      templateFile: "",
      overwriteExistingFile: Boolean(selectedTemplate?.name),
    });
  }, [selectedTemplate]);

  const uploadTemplate = (file) => {
    setFilename(file.name);
    var reader = new FileReader();
    reader.onload = function (e) {
      var binaryString = e.target.result;
      let arrayBuffer = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        arrayBuffer[i] = binaryString.charCodeAt(i);
      }

      const formData = new FormData();
      formData.append("report_template_name", templateName);
      formData.append("report_template_description", templateDescription);
      formData.append("template_binary", file);

      setFileUploaded(true);
      setFileAccepted(true); // hardcoded for now, we can add template validation functionality later if desired
      setTemplateFile(formData);

      // included here because readAsBinaryString is async
      props.onChange({
        templateName: templateName,
        templateDescription: templateDescription,
        templateFile: formData,
        overwriteExistingFile: Boolean(selectedTemplate),
      });
    };
    reader.readAsBinaryString(file);
  };
  return (
    <CForm>
      <CFormGroup row>
        <CLabel className="col-sm-3 col-form-label">Template (Optional)</CLabel>
        <CCol sm={9}>
          <ListSelect
            handleSelectedValue={(x) => {
              handleTemplatedChanged(x);
            }}
            items={isLoading ? [{ name: "LOADING", id: 0 }] : data}
          />
        </CCol>
      </CFormGroup>
      {!selectedTemplate && (
        <CFormGroup row>
          <CLabel className="col-sm-3 col-form-label">Name</CLabel>
          <CCol sm={9}>
            {props.mode === "create" ? (
              <CInput
                id="edit-report-name-template-input"
                type="text"
                name="name"
                placeholder="Name"
                value={templateName}
                onChange={(e) => {
                  setTemplateName(e.target.value);
                  props.onChange({
                    templateName: e.target.value,
                    templateDescription: templateDescription,
                    templateFile: templateFile,
                    overwriteExistingFile: Boolean(selectedTemplate),
                  });
                }}
              />
            ) : (
              <CLabel
                id="edit-report-name-label"
                className="col-sm-9 col-form-label"
              >
                {templateName}
              </CLabel>
            )}
          </CCol>
        </CFormGroup>
      )}
      {selectedTemplate && (
        <CAlert color="warning">
          You are about to overwrite an existing template. Any{" "}
          {props.reportType === ReportType.ANALYTIC
            ? "report"
            : "Fast Draft generated documents"}{" "}
          using this template will be updated to use the new template.
        </CAlert>
      )}
      <CFormGroup row>
        <CLabel className="col-sm-3 col-form-label">Description</CLabel>
        {props.mode === "view" ? (
          <CLabel
            id="edit-report-description-label"
            className="col-sm-9 col-form-label"
          >
            {templateDescription}
          </CLabel>
        ) : (
          <CCol sm={9}>
            <CTextarea
              id="edit-report-description-textarea"
              type="text"
              rows="2"
              name="description"
              placeholder="Description"
              value={templateDescription}
              onChange={(e) => {
                setTemplateDescription(e.target.value);
                props.onChange({
                  templateName: templateName,
                  templateDescription: e.target.value,
                  templateFile: templateFile,
                  overwriteExistingFile: Boolean(selectedTemplate),
                });
              }}
            />
          </CCol>
        )}
      </CFormGroup>
      <CFormGroup row>
        <CLabel className="col-sm-3 col-form-label">Template</CLabel>
        <CCol sm={9}>
          <input
            type="file"
            accept=".docx"
            style={{ display: "none" }}
            ref={fileInput}
            onChange={(event) => {
              uploadTemplate(event.target.files[0]);
              event.target.value = null;
            }}
          />
          <CButton
            color="primary"
            style={{ float: "right" }}
            onClick={() => fileInput.current.click()}
          >
            Choose Template
          </CButton>
        </CCol>
      </CFormGroup>
      {fileUploaded && fileAccepted && (
        <CAlert type="success" color="success" title="File Accepted">
          {filename} accepted
        </CAlert>
      )}
      {fileUploaded && !fileAccepted && (
        <CAlert type="error" color="danger" title="File not accepted">
          {filename} not accepted
        </CAlert>
      )}
      {/* 
      
        This CAlert is here for when/if we want to have an alert for when a template is invalid

      {invalidBucketSelectionMessage && (
        <CFormGroup row>
          <div>
            <CAlert color="info" style={{ margin: "0px 10px 0px 10px" }}>
              {`${invalidBucketSelectionMessage}`}
            </CAlert>
          </div>
        </CFormGroup>
      )} */}
    </CForm>
  );
};

ReportTemplateEditor.propTypes = {
  mode: PropTypes.oneOf(["create", "edit", "view"]).isRequired,
  report: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    bucket_fks: PropTypes.arrayOf(PropTypes.string).isRequired,
    report_template_fk: PropTypes.string.isRequired,
  }),
  onChange: PropTypes.func.isRequired,
  reportType: PropTypes.oneOf([ReportType.ANALYTIC, ReportType.GENERATIVE]),
};

ReportTemplateEditor.defaultProps = {
  reportType: ReportType.ANALYTIC,
  report: {
    name: "",
    description: "",
    parent_fks: [],
    report_template_fk: "",
  },
};

export const SelectTemplateModal = (props) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [generateDocData, setGenerateDocData] = useState(null);

  const { data, refetch, isFetching, isError } = useGenerateTemplateDoc({
    reportTemplateId: selectedTemplate?.id,
    datasetId: props.datasetId,
    sourceUid: props.sourceUid,
  });

  const resetInitialState = () => {
    setSelectedTemplate(null);
    setGenerateDocData(null);
    setErrorMessage(null);
  };

  const resetAfterClose = () => {
    resetInitialState();
    setSuccessMessage(null);
  };

  useEffect(() => {
    if (isError) {
      setErrorMessage(
        "An error occurred while generating the report. Please try again.",
      );
    } else {
      setErrorMessage(null);
    }
  }, [isError]);

  useEffect(() => {
    if (isFetching) {
      setSuccessMessage(null);
    }
  }, [isFetching]);

  useEffect(() => {
    console.log("generateDocData", generateDocData);
    if (generateDocData) {
      let blob = new Blob([data], {
        // type docx
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `Nuclearn-Report-${selectedTemplate.name}.docx`;
      link.click();
      setSuccessMessage(
        `Report has been generated using template ${selectedTemplate.name} for SourceUID ${props.sourceUid}`,
      );
      resetInitialState();
    }
  }, [generateDocData]);

  useEffect(() => {
    if (data && selectedTemplate) {
      setGenerateDocData(data);
    }
  }, [data]);

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  const readyToSubmit = (template) => {
    return template?.id !== null;
  };

  const onSubmit = () => {
    refetch();
  };

  const modalContent = (
    <CModal
      show={props.isVisible}
      onClose={onClose}
      style={{ textAlign: "left" }}
      size="lg"
      backdrop="static"
      keyboard={false}
    >
      <CModalHeader>
        <CCol sm="9">
          <h4>Select a Template</h4>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="select-template-close-button"
            color="secondary"
            onClick={onClose}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <LoadingOverlay
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Uploading template..."
      >
        {selectedTemplate && generateDocData ? (
          <CModalBody>
            <span id="select-template-success-message">
              Template {selectedTemplate?.name} has been selected!
              <br />
              <br />
            </span>
          </CModalBody>
        ) : (
          <>
            <CModalBody>
              {errorMessage && !isFetching && (
                <CAlert color="danger">{errorMessage}</CAlert>
              )}
              {successMessage && !isFetching && (
                <CAlert color="success">{successMessage}</CAlert>
              )}
              <ReportTemplateSelector
                reportType={props.type}
                onChange={(template) => setSelectedTemplate(template)}
              />
            </CModalBody>
            <CModalFooter>
              <CCol sm="12" style={{ textAlign: "right" }}>
                {isFetching ? (
                  <PulseLoader color="#5BB3D6" />
                ) : (
                  <CButton
                    id="submit-select-template-button"
                    color="success"
                    onClick={onSubmit}
                    disabled={!readyToSubmit(selectedTemplate)}
                  >
                    Generate Doc
                  </CButton>
                )}
              </CCol>
            </CModalFooter>
          </>
        )}
      </LoadingOverlay>
    </CModal>
  );

  if (!props.isVisible) {
    return null;
  }

  return createPortal(modalContent, document.body);
};

SelectTemplateModal.propTypes = {
  datasetId: PropTypes.number.isRequired,
  sourceUid: PropTypes.string.isRequired,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["ANALYTIC", "GENERATIVE"]).isRequired, // Update ReportType enums if necessary
};

export const CreateReportTemplateModal = (props) => {
  const [reportTemplate, setReportTemplate] = useState(null);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const {
    mutate: uploadReportTemplate,
    isLoading: isCreateReportLoading,
    status: createReportStatus,
    isError: isCreateReportError,
    error: createReportError,
    reset: resetCreateReport,
  } = useUploadReportTemplate();

  const resetAfterClose = () => {
    setReportTemplate(null);
    setErrorMessage(null);
    resetCreateReport();
  };
  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };
  // If there was a error creating the report, get rid of it!
  if (isCreateReportError) {
    let newErrorMessage = String(createReportError);
    if (createReportError?.response?.data?.detail) {
      newErrorMessage =
        "Server Error " +
        createReportError.response.status +
        ": " +
        JSON.stringify(createReportError.response.data.detail);
    }
    if (newErrorMessage !== errorMessage) {
      setErrorMessage(newErrorMessage);
    }
  }

  const readyToSumbit = (reportTemplate) => {
    if (!reportTemplate) {
      return false;
    }

    const { templateName, templateFile, overwriteExistingFile } =
      reportTemplate;

    if (!templateName || !templateFile || overwriteExistingFile === null) {
      return false;
    }
    return true;
  };

  const onSubmit = () => {
    uploadReportTemplate({ reportTemplate, reportType: props.type });
  };

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h4>
            {props.type === ReportType.ANALYTIC
              ? "Create a New Report Template"
              : "Create a New Generative Template"}
          </h4>
          <span style={{ marginTop: "10px" }}>
            {props.type === ReportType.Analytic
              ? "Create and save a new Nuclearn report template for use in future reporting."
              : "Create and save a new Nuclearn generative template to be completed by generative AI."}
          </span>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="create-report-close-button"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <LoadingOverlay
        active={isCreateReportLoading}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Creating report..."
      >
        {createReportStatus === "success" ? (
          <CModalBody>
            <span id="create-report-success-message">
              Report template {reportTemplate.templateName} has been created!
              <br />
              <br />
            </span>
          </CModalBody>
        ) : (
          <>
            <CModalBody>
              {errorMessage && <CAlert color="danger">{errorMessage}</CAlert>}
              <ReportTemplateEditor
                reportType={props.type}
                mode="create"
                onChange={(report) => setReportTemplate(report)}
              />
            </CModalBody>
            <CModalFooter>
              <CCol sm="12" style={{ textAlign: "right" }}>
                <CButton
                  id="submit-create-report-button"
                  color="success"
                  onClick={() => onSubmit()}
                  disabled={!readyToSumbit(reportTemplate)}
                >
                  {reportTemplate?.overwriteExistingFile
                    ? "Update Template"
                    : "Create New Template"}
                </CButton>
              </CCol>
            </CModalFooter>
          </>
        )}
      </LoadingOverlay>
    </CModal>
  );
};

CreateReportTemplateModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  type: PropTypes.oneOf([ReportType.ANALYTIC, ReportType.GENERATIVE])
    .isRequired,
};

export const CreateReportModal = (props) => {
  const [report, setReport] = useState(null);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const {
    mutate: createReport,
    isLoading: isCreateReportLoading,
    status: createReportStatus,
    isError: isCreateReportError,
    error: createReportError,
    data: newReport,
    reset: resetCreateReport,
  } = useCreateReport();

  const resetAfterClose = () => {
    setReport(null);
    setErrorMessage(null);
    resetCreateReport();
  };
  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };
  // If there was a error creating the report, get rid of it!
  if (isCreateReportError) {
    let newErrorMessage = String(createReportError);
    if (createReportError?.response?.data?.detail) {
      newErrorMessage =
        "Server Error " +
        createReportError.response.status +
        ": " +
        JSON.stringify(createReportError.response.data.detail);
    }
    if (newErrorMessage !== errorMessage) {
      setErrorMessage(newErrorMessage);
    }
  }

  const onSubmit = () => {
    let errors = validateReport(report);
    if (errors.length === 0) {
      setErrorMessage(null);
      createReport(report);
    } else {
      setErrorMessage(errors.map((error, index) => <p key={index}>{error}</p>));
    }
  };

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      style={{ textAlign: "left" }}
      data-testid="create-report-dialog"
    >
      <CModalHeader>
        <CCol sm="9">
          <h4>Create a New Report</h4>
          <span style={{ marginTop: "10px" }}>
            {`Create and save a new ${determineLoginPlatformType()} report for use in future reporting.`}
          </span>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="create-report-close-button"
            data-testid="create-report-close-btn"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <LoadingOverlay
        active={isCreateReportLoading}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Creating report..."
      >
        {createReportStatus === "success" ? (
          <CModalBody>
            <span id="create-report-success-message">
              {newReport.name} has been created!
              <br />
              <br />
              {/* <ReportDetailsButton variant={null} report={newReport} /> */}
            </span>
          </CModalBody>
        ) : (
          <>
            <CModalBody>
              {errorMessage && <CAlert color="danger">{errorMessage}</CAlert>}
              <ReportEditor
                reportType={props.reportType}
                mode="create"
                onChange={(_report) => setReport(_report)}
              />
            </CModalBody>
            <CModalFooter>
              <CCol sm="12" style={{ textAlign: "right" }}>
                <CButton
                  id="submit-create-report-button"
                  data-testid="create-report-submit-btn"
                  color="success"
                  onClick={() => onSubmit()}
                >
                  Create New Report
                </CButton>
              </CCol>
            </CModalFooter>
          </>
        )}
      </LoadingOverlay>
    </CModal>
  );
};

CreateReportModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  reportType: PropTypes.oneOf([ReportType.ANALYTIC, ReportType.GENERATIVE]),
};

export const DeleteReportModal = (props) => {
  const [confirmDelete, setConfirmDelete] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState(null);
  const {
    mutate: deleteReport,
    isLoading: isDeleteReportLoading,
    status: deleteReportStatus,
    isError: isDeleteReportError,
    error: deleteReportError,
    reset: resetDeleteReport,
  } = useDeleteReport();

  const resetAfterClose = () => {
    setConfirmDelete("");
    setErrorMessage(null);
    resetDeleteReport();
  };

  // If there was a error deleting the dataset, show message
  if (isDeleteReportError) {
    if (String(deleteReportError.response.data.detail) !== errorMessage) {
      setErrorMessage(String(deleteReportError.response.data.detail));
    }
  }

  const onSubmit = (reportId) => {
    setErrorMessage(null);
    deleteReport(reportId);
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  return (
    <DeleteItemModal
      isVisible={props.isVisible}
      onClose={onClose}
      itemName={props.reportName}
      itemId={props.id}
      onSubmit={onSubmit}
      confirmDeleteString={confirmDelete}
      setConfirmDeleteString={setConfirmDelete}
      errorMessage={errorMessage}
      deleteItemStatus={deleteReportStatus}
      isDeleteItemLoading={isDeleteReportLoading}
      modalTitle="Delete Existing Report"
      testId={`delete-report-dialog-${props.id}`}
      deleteInstructions="CONFIRM YOU WANT TO DELETE THIS REPORT BY TYPING REPORT NAME HERE, THEN CLICK DELETE"
      buttonTitle="Delete Report"
    />
  );
};

DeleteReportModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  reportName: PropTypes.string,
  id: PropTypes.number,
};

export const UpdateReportModal = (props) => {
  const [reportDescription, setReportDescription] = React.useState("");
  const [reportAllowOthers, setReportAllowOthers] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const {
    mutate: updateReport,
    isLoading: isUpdateReportLoading,
    status: updateReportStatus,
    isError: isUpdateReportError,
    error: updateReportError,
    reset: resetUpdateReport,
  } = useUpdateReport();

  useEffect(() => {
    setReportDescription(props.reportDescription);
    setReportAllowOthers(props.reportAllowOthersToEdit);
  }, [props]);

  const resetAfterClose = () => {
    setReportDescription("");
    setReportAllowOthers(false);
    setErrorMessage(null);
    resetUpdateReport();
  };
  // If there was a error creating the dataset, get rid of it!
  if (isUpdateReportError) {
    if (String(updateReportError.response.data.detail) !== errorMessage) {
      setErrorMessage(String(updateReportError.response.data.detail));
    }
  }
  const onSubmit = (reportId, description, allow_others_to_edit) => {
    setErrorMessage(null);
    updateReport({
      reportId,
      report: {
        description: description,
        allow_others_to_edit: allow_others_to_edit,
      },
    });
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h3>Update existing report</h3>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="update-report-close-button"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <LoadingOverlay
        active={isUpdateReportLoading}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Updating report..."
      >
        {updateReportStatus === "success" ? (
          <CModalBody>
            <span id="update-report-success-message-span">Updated report</span>
          </CModalBody>
        ) : (
          <>
            <CModalBody>
              {errorMessage && <CAlert color="danger">{errorMessage}</CAlert>}
              <CForm>
                <CFormGroup row>
                  <CLabel className="col-sm-2 col-form-label">Name</CLabel>
                  <CLabel
                    id="update-report-name-label"
                    className="col-sm-10 col-form-label"
                  >
                    {props.reportName}
                  </CLabel>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel className="col-sm-2 col-form-label">
                    Description
                  </CLabel>
                  <CCol sm={10}>
                    <CTextarea
                      id="update-report-description-textarea"
                      type="text"
                      rows="2"
                      name="description"
                      placeholder="Description"
                      value={
                        reportDescription === null ? "" : reportDescription
                      }
                      onChange={(e) => setReportDescription(e.target.value)}
                    />
                  </CCol>
                </CFormGroup>
                {/* TODO: This is currently un-used, determine if this is needed. If so backend route needs updated. */}
                {/* <CFormGroup inline>
                  <CLabel htmlFor="allow_others_to_edit">
                    Allow others to edit
                  </CLabel>
                  <CInputCheckbox
                    id="update-report-allow-others-checkbox"
                    type="checkbox"
                    name="allow_others_to_edit"
                    style={{ marginLeft: "10px" }}
                    checked={!!reportAllowOthers}
                    onChange={(e) => setReportAllowOthers(e.target.checked)}
                  />
                </CFormGroup> */}
              </CForm>
            </CModalBody>
            <CModalFooter>
              <CCol sm="12" style={{ textAlign: "right" }}>
                <CButton
                  id="update-report-submit-button"
                  color="success"
                  onClick={() =>
                    onSubmit(props.id, reportDescription, reportAllowOthers)
                  }
                >
                  Update Report
                </CButton>
              </CCol>
            </CModalFooter>
          </>
        )}
      </LoadingOverlay>
    </CModal>
  );
};

UpdateReportModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.number,
  reportName: PropTypes.string,
  reportDescription: PropTypes.string,
  reportAllowOthersToEdit: PropTypes.bool,
};

const downloadGenAIDoc = async (artifact) => {
  console.log("Clicked listItem");
  // we want to prompt a download for the user
  const fileData = {
    url: `${nuclearn_api_url}/artifacts/${artifact.id}/download`,
    httpHeaders: authorizedUser(),
  };
  // Construct the API URL
  const fileUrl = fileData.url;
  console.log(fileData.httpHeaders);
  // Fetch the file with the appropriate headers
  const response = await fetch(fileUrl, {
    method: "GET",
    headers: {
      Authorization: fileData.httpHeaders.Authorization,
    },
  });

  // Check if the response is okay
  if (response.ok) {
    // Convert the response into a Blob
    const blob = await response.blob();
    // print the blob filename
    console.log(blob.name);
    console.log(blob);

    // Create a URL for the Blob
    const blobUrl = URL.createObjectURL(blob);

    // Create a download link and trigger it
    const downloadLink = document.createElement("a");
    downloadLink.href = blobUrl;
    // File Type Docx Always for now
    downloadLink.download = artifact.name + ".docx";
    // add the filetype to the download link
    document.body.appendChild(downloadLink);
    downloadLink.click();

    // Clean up
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(blobUrl);
  }
};

export const UpdateReportTemplateModal = (props) => {
  const [reportDescription, setReportDescription] = React.useState("");
  const [reportAllowOthers, setReportAllowOthers] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const {
    mutate: updateReportTemplate,
    isLoading: isUpdateReportTemplateLoading,
    status: updateReportTemplateStatus,
    isError: isUpdateReportTemplateError,
    error: updateReportTemplateError,
    reset: resetUpdateTemplateReport,
  } = useUpdateReportTemplate();

  useEffect(() => {
    setReportDescription(props.reportDescription);
    setReportAllowOthers(props.reportAllowOthersToEdit);
  }, [props]);

  const resetAfterClose = () => {
    setReportDescription("");
    setReportAllowOthers(false);
    setErrorMessage(null);
    resetUpdateTemplateReport();
  };
  // If there was a error creating the dataset, get rid of it!
  if (isUpdateReportTemplateError) {
    if (
      String(updateReportTemplateError.response.data.detail) !== errorMessage
    ) {
      setErrorMessage(String(updateReportTemplateError.response.data.detail));
    }
  }
  const onSubmit = (reportTemplateId, description, allow_others_to_edit) => {
    setErrorMessage(null);
    updateReportTemplate({
      reportTemplateId,
      reportTemplate: {
        description: description,
        allow_others_to_edit: allow_others_to_edit,
      },
    });
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h3>Update existing template</h3>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="update-report-close-button"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <LoadingOverlay
        active={isUpdateReportTemplateLoading}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Updating report..."
      >
        {updateReportTemplateStatus === "success" ? (
          <CModalBody>
            <span id="update-report-success-message-span">
              Updated template
            </span>
          </CModalBody>
        ) : (
          <>
            <CModalBody>
              {errorMessage && <CAlert color="danger">{errorMessage}</CAlert>}
              <CForm>
                <CFormGroup row>
                  <CLabel className="col-sm-2 col-form-label">Name</CLabel>
                  <CLabel
                    id="update-report-name-label"
                    className="col-sm-10 col-form-label"
                  >
                    {props.reportName}
                  </CLabel>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel className="col-sm-2 col-form-label">
                    Description
                  </CLabel>
                  <CCol sm={10}>
                    <CTextarea
                      id={`update-report-description-textarea-${props.name}`}
                      type="text"
                      rows="2"
                      name="description"
                      placeholder="Description"
                      value={
                        reportDescription === null ? "" : reportDescription
                      }
                      onChange={(e) => setReportDescription(e.target.value)}
                    />
                  </CCol>
                </CFormGroup>
                {/* TODO: This is currently un-used, determine if this is needed. If so backend route needs updated. */}
                {/* <CFormGroup inline>
                  <CLabel htmlFor="allow_others_to_edit">
                    Allow others to edit
                  </CLabel>
                  <CInputCheckbox
                    id="update-report-allow-others-checkbox"
                    type="checkbox"
                    name="allow_others_to_edit"
                    style={{ marginLeft: "10px" }}
                    checked={!!reportAllowOthers}
                    onChange={(e) => setReportAllowOthers(e.target.checked)}
                  />
                </CFormGroup> */}
              </CForm>
            </CModalBody>
            <CModalFooter>
              <CCol sm="12" style={{ textAlign: "right" }}>
                <CButton
                  id="update-report-submit-button"
                  color="success"
                  onClick={() =>
                    onSubmit(props.id, reportDescription, reportAllowOthers)
                  }
                >
                  Update Template
                </CButton>
              </CCol>
            </CModalFooter>
          </>
        )}
      </LoadingOverlay>
    </CModal>
  );
};

UpdateReportTemplateModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.number,
  reportName: PropTypes.string,
  reportDescription: PropTypes.string,
  reportAllowOthersToEdit: PropTypes.bool,
  name: PropTypes.string,
};

function initializeDisplayItems(setDisplayItems, bucketList) {
  setDisplayItems(
    bucketList?.map((bucket) => {
      return {
        id: bucket.id,
        name: bucket.name,
      };
    }),
  );
}

export const DeleteReportTemplateModal = (props) => {
  const [confirmDelete, setConfirmDelete] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState(null);
  const {
    mutate: deleteReportTemplate,
    isLoading: isDeleteReportTemplateLoading,
    status: deleteReportTemplateStatus,
    isError: isDeleteReportTemplateError,
    error: deleteReportTemplateError,
    reset: resetDeleteReportTemplate,
  } = useDeleteReportTemplate();

  const resetAfterClose = () => {
    setConfirmDelete("");
    setErrorMessage(null);
    resetDeleteReportTemplate();
  };

  // If there was a error deleting the dataset, show message
  if (isDeleteReportTemplateError) {
    if (
      String(deleteReportTemplateError.response.data.detail) !== errorMessage
    ) {
      setErrorMessage(String(deleteReportTemplateError.response.data.detail));
    }
  }

  const onSubmit = (reportId) => {
    setErrorMessage(null);
    deleteReportTemplate(reportId);
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  return (
    <DeleteItemModal
      isVisible={props.isVisible}
      onClose={onClose}
      itemName={props.reportName}
      itemId={props.id}
      onSubmit={onSubmit}
      confirmDeleteString={confirmDelete}
      setConfirmDeleteString={setConfirmDelete}
      errorMessage={errorMessage}
      deleteItemStatus={deleteReportTemplateStatus}
      isDeleteItemLoading={isDeleteReportTemplateLoading}
      modalTitle="Delete Existing Template"
      deleteInstructions="CONFIRM YOU WANT TO DELETE THIS TEMPLATE BY TYPING REPORT NAME HERE, THEN CLICK DELETE"
      buttonTitle="Delete Template"
    />
  );
};

DeleteReportTemplateModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  reportName: PropTypes.string,
  id: PropTypes.number,
};

export const ReportArtifactListModal = (props) => {
  const [artifacts, setArtifacts] = useState([]);
  const [error, setError] = useState(null);

  const {
    data: reportTemplateArtifactData,
    isLoading: reportTemplateArtifactLoading,
    isError: reportTemplateArtifactError,
    refetch,
    status: reportTemplateArtifactStatus,
  } = useGetReportTemplateArtifacts(props?.row?.id);

  useEffect(() => {
    if (reportTemplateArtifactError) {
      setError("Failed to fetch artifacts. Please try again.");
    } else {
      setError(null);
      setArtifacts(reportTemplateArtifactData || []);
    }
  }, [reportTemplateArtifactData, reportTemplateArtifactError]);

  const handleRetry = () => {
    refetch();
  };

  return (
    <Dialog
      open={props.isVisible}
      onClose={props.onClose}
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6">
              AI Generated Documents for template - {props?.row?.name}
            </Typography>
          </Grid>
          <Grid item></Grid>
        </Grid>
      </DialogTitle>
      {/* classname min width and height to 75px */}
      <DialogContent
        dividers
        style={{ minHeight: "150px", minWidth: "150px", height: "100%" }}
      >
        <ReactQueryLoading
          isLoading={reportTemplateArtifactLoading}
          status={reportTemplateArtifactStatus}
          error={reportTemplateArtifactError}
        >
          {error && (
            <Alert severity="error">
              {error}{" "}
              <Button color="inherit" onClick={handleRetry}>
                Retry
              </Button>
            </Alert>
          )}
          {!reportTemplateArtifactLoading && !error && (
            <List
              style={{
                display: "flex",
                width: "100%",
                flexWrap: "wrap",
              }}
            >
              {artifacts.map((artifact, index) => {
                return (
                  <GenDocPreview
                    key={artifact.name + "-" + index}
                    artifact={artifact}
                  />
                );
              })}
            </List>
          )}
        </ReactQueryLoading>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ReportArtifactListModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.object.isRequired,
};

const GenDocPreview = ({ artifact }) => {
  const { data: fileBlobData } = useDownloadDocumentPreview(artifact?.id);
  return (
    <ListItem
      style={{
        flex: "0 0 50%",
      }}
      onCLick={() => {
        console.log("test");
      }}
      key={artifact.id}
    >
      <Box sx={{ width: "auto", overflow: "auto" }}>
        <Paper
          onClick={() => {
            downloadGenAIDoc(artifact);
          }}
          variant="outlined"
          sx={{ padding: 2, wordWrap: "break-word" }}
        >
          <PDFViewer file={fileBlobData} windowHeight={400} pageNum={1} />
        </Paper>
      </Box>{" "}
    </ListItem>
  );
};

GenDocPreview.propTypes = {
  artifact: PropTypes.object.isRequired,
};
